import { useState, type ReactNode } from 'react';
import { Card } from '@lineageatlas/design-system';

export type ProvenanceLevel = 'clinical' | 'qc' | 'inventory' | 'ai' | 'reference';
export type Tier = 'gold' | 'silver' | 'watch' | 'hold';
export type LoopStageState = 'complete' | 'active' | 'pending' | 'blocked';
export type ReviewerState = 'draft' | 'review' | 'approved' | 'blocked';

const provenanceStyles: Record<
  ProvenanceLevel,
  { label: string; color: string; description: string }
> = {
  clinical: {
    label: 'Clinical',
    color: '#8b5cf6',
    description: 'Assignments, patient context, and release-facing records',
  },
  qc: {
    label: 'QC',
    color: '#00d4aa',
    description: 'Marker panels, viability, sterility, drift, and review state',
  },
  inventory: {
    label: 'Inventory',
    color: '#fbbf24',
    description: 'Vials, passages, stock thresholds, and lot availability',
  },
  ai: {
    label: 'AI assist',
    color: '#60a5fa',
    description: 'Generated summaries only; human review remains required',
  },
  reference: {
    label: 'Reference',
    color: '#a78bfa',
    description: 'Citable publications, standards, protocols, and external evidence',
  },
};

const tierStyles: Record<Tier, { label: string; color: string; background: string }> = {
  gold: { label: 'Tier 1', color: '#00d4aa', background: 'rgba(0, 212, 170, 0.12)' },
  silver: { label: 'Tier 2', color: '#60a5fa', background: 'rgba(96, 165, 250, 0.12)' },
  watch: { label: 'Watch', color: '#fbbf24', background: 'rgba(251, 191, 36, 0.12)' },
  hold: { label: 'Hold', color: '#ff6b6b', background: 'rgba(255, 107, 107, 0.12)' },
};

const stageStyles: Record<LoopStageState, { color: string; label: string }> = {
  complete: { color: '#00d4aa', label: 'Complete' },
  active: { color: '#60a5fa', label: 'Active' },
  pending: { color: 'var(--color-text-muted)', label: 'Pending' },
  blocked: { color: '#ff6b6b', label: 'Blocked' },
};

const reviewerStateStyles: Record<ReviewerState, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'var(--color-text-muted)' },
  review: { label: 'In review', color: '#60a5fa' },
  approved: { label: 'Approved', color: '#00d4aa' },
  blocked: { label: 'Blocked', color: '#ff6b6b' },
};

export function ProvenanceLegend({
  levels = ['clinical', 'qc', 'inventory', 'ai'],
}: {
  levels?: ProvenanceLevel[];
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }} aria-label="Evidence provenance legend">
      {levels.map((level) => {
        const item = provenanceStyles[level];
        return (
          <div
            key={level}
            style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '9999px',
                background: item.color,
                boxShadow: `0 0 16px ${item.color}`,
                marginTop: '6px',
                flex: '0 0 auto',
              }}
            />
            <span style={{ minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  color: 'var(--color-text)',
                  fontSize: 'var(--text-caption)',
                  fontWeight: 700,
                }}
              >
                {item.label}
              </span>
              <span
                style={{
                  display: 'block',
                  color: 'var(--color-text-muted)',
                  fontSize: 'var(--text-xs)',
                  lineHeight: 1.4,
                }}
              >
                {item.description}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function TierBadge({ tier, children }: { tier: Tier; children?: ReactNode }) {
  const style = tierStyles[tier];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 'var(--radius-full)',
        border: `1px solid ${style.color}`,
        background: style.background,
        color: style.color,
        padding: '3px 10px',
        fontSize: 'var(--text-xs)',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {children ?? style.label}
    </span>
  );
}

export interface LoopStage {
  id: string;
  label: string;
  detail?: string;
  state: LoopStageState;
}

export function LoopStageTimeline({ stages }: { stages: LoopStage[] }) {
  return (
    <ol
      style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-3)' }}
    >
      {stages.map((stage, index) => {
        const style = stageStyles[stage.state];
        return (
          <li
            key={stage.id}
            style={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: 'var(--space-3)' }}
          >
            <span
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              aria-hidden="true"
            >
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '9999px',
                  background: style.color,
                  boxShadow: stage.state === 'active' ? `0 0 18px ${style.color}` : undefined,
                  marginTop: '3px',
                }}
              />
              {index < stages.length - 1 && (
                <span
                  style={{
                    width: '1px',
                    minHeight: '32px',
                    flex: 1,
                    background: 'var(--glass-border-color)',
                    marginTop: '6px',
                  }}
                />
              )}
            </span>
            <span style={{ minWidth: 0 }}>
              <span
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 'var(--space-2)',
                  color: 'var(--color-text)',
                  fontWeight: 700,
                }}
              >
                <span>{stage.label}</span>
                <span
                  style={{
                    color: style.color,
                    fontSize: 'var(--text-xs)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {style.label}
                </span>
              </span>
              {stage.detail && (
                <span
                  style={{
                    display: 'block',
                    color: 'var(--color-text-muted)',
                    fontSize: 'var(--text-caption)',
                    lineHeight: 1.4,
                    marginTop: '2px',
                  }}
                >
                  {stage.detail}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export interface EvidenceDrawerItem {
  id: string;
  label: string;
  source: ProvenanceLevel;
  detail: ReactNode;
}

export interface AIEvidenceReference {
  source: string;
  title: string;
  snippet: string;
  url: string;
  provenance: ProvenanceLevel;
}

export interface AIFindingReferenceInput {
  source?: string | null;
  title?: string | null;
  snippet?: string | null;
  url?: string | null;
  provenance?: ProvenanceLevel | null;
}

export function normalizeAIEvidenceReferences(
  references: AIFindingReferenceInput[] | null | undefined,
): AIEvidenceReference[] {
  return (references ?? [])
    .filter(
      (reference) => reference.title || reference.snippet || reference.url || reference.source,
    )
    .map((reference, index) => ({
      source: reference.source?.trim() || `Reference ${index + 1}`,
      title:
        reference.title?.trim() || reference.source?.trim() || `Evidence reference ${index + 1}`,
      snippet:
        reference.snippet?.trim() ||
        'No excerpt provided. Open the source and verify before using this finding.',
      url: reference.url?.trim() || '',
      provenance: reference.provenance ?? 'ai',
    }));
}

export function aiReferencesToEvidenceDrawerItems(
  references: AIEvidenceReference[],
): EvidenceDrawerItem[] {
  return references.map((reference, index) => ({
    id: `ai-evidence-${index}-${reference.title}`,
    label: reference.title,
    source: reference.provenance,
    detail: (
      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <span style={{ color: 'var(--color-text)', fontWeight: 700 }}>{reference.title}</span>
        <span>{reference.snippet}</span>
        <span style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
          Source: {reference.source}
        </span>
        {reference.url && (
          <a
            href={reference.url}
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--color-accent-primary)', fontSize: 'var(--text-xs)' }}
          >
            Open evidence source
          </a>
        )}
      </div>
    ),
  }));
}

export function EvidenceCitationList({ references }: { references: AIEvidenceReference[] }) {
  if (references.length === 0) return null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }} aria-label="Show your work citations">
      {references.map((reference, index) => (
        <a
          key={`${reference.title}-${index}`}
          href={reference.url || undefined}
          target={reference.url ? '_blank' : undefined}
          rel={reference.url ? 'noreferrer' : undefined}
          aria-disabled={!reference.url}
          onClick={(event) => {
            if (!reference.url) event.preventDefault();
          }}
          style={{
            display: 'grid',
            gap: '2px',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--glass-border-color)',
            background: 'rgba(255,255,255,0.03)',
            color: 'var(--color-text)',
            textDecoration: 'none',
            cursor: reference.url ? 'pointer' : 'default',
          }}
        >
          <span style={{ fontSize: 'var(--text-caption)', fontWeight: 700 }}>
            {reference.title}
          </span>
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
              lineHeight: 1.4,
            }}
          >
            {reference.snippet}
          </span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
            [{index + 1}] {provenanceStyles[reference.provenance].label} · {reference.source}
          </span>
        </a>
      ))}
    </div>
  );
}

export function QCCopilotAssistPanel({
  reviewFocus,
  preview = false,
}: {
  reviewFocus: string[];
  preview?: boolean;
}) {
  return (
    <div
      role="note"
      aria-label="QC co-pilot assistive review note"
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid rgba(96, 165, 250, 0.35)',
        background: 'rgba(96, 165, 250, 0.08)',
        marginTop: 'var(--space-4)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
        }}
      >
        <strong style={{ color: 'var(--color-text)', fontSize: 'var(--text-caption)' }}>
          QC co-pilot: second-review assist
        </strong>
        {preview && <TierBadge tier="watch">Preview only</TierBadge>}
      </div>
      <p
        style={{
          color: 'var(--color-text-muted)',
          fontSize: 'var(--text-caption)',
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        Assistive summary only. A qualified reviewer must verify evidence and make all release,
        hold, or quarantine decisions.
      </p>
      {reviewFocus.length > 0 && (
        <ul
          style={{
            margin: 0,
            paddingLeft: 'var(--space-5)',
            color: 'var(--color-text)',
            fontSize: 'var(--text-caption)',
            lineHeight: 1.5,
          }}
        >
          {reviewFocus.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function EvidenceDrawerHost({
  title = 'Evidence drawer',
  items,
}: {
  title?: string;
  items: EvidenceDrawerItem[];
}) {
  const [selectedId, setSelectedId] = useState(items[0]?.id ?? '');
  const selected = items.find((item) => item.id === selectedId) ?? items[0];

  if (items.length === 0) {
    return (
      <Card>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
          No evidence available.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-3)',
          alignItems: 'center',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-h4)',
            color: 'var(--color-text)',
          }}
        >
          {title}
        </h3>
        {selected && (
          <TierBadge tier={selected.source === 'ai' ? 'watch' : 'silver'}>
            {provenanceStyles[selected.source].label}
          </TierBadge>
        )}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(120px, 0.8fr) minmax(0, 1.2fr)',
          gap: 'var(--space-4)',
        }}
      >
        <div style={{ display: 'grid', gap: 'var(--space-2)', alignContent: 'start' }}>
          {items.map((item) => {
            const isSelected = item.id === selected?.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedId(item.id)}
                style={{
                  textAlign: 'left',
                  border: `1px solid ${isSelected ? provenanceStyles[item.source].color : 'var(--glass-border-color)'}`,
                  borderRadius: 'var(--radius-md)',
                  background: isSelected ? 'rgba(255,255,255,0.06)' : 'transparent',
                  color: 'var(--color-text)',
                  padding: 'var(--space-2) var(--space-3)',
                  cursor: 'pointer',
                  font: 'inherit',
                  fontSize: 'var(--text-caption)',
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        <div
          style={{
            borderLeft: '1px solid var(--glass-border-color)',
            paddingLeft: 'var(--space-4)',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--text-caption)',
            lineHeight: 1.5,
          }}
        >
          {selected?.detail}
        </div>
      </div>
    </Card>
  );
}

export function ReviewerStateControls({
  value,
  onChange,
}: {
  value: ReviewerState;
  onChange: (value: ReviewerState) => void;
}) {
  const states: ReviewerState[] = ['draft', 'review', 'approved', 'blocked'];
  return (
    <div
      role="group"
      aria-label="Reviewer state"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}
    >
      {states.map((state) => {
        const style = reviewerStateStyles[state];
        const active = value === state;
        return (
          <button
            key={state}
            type="button"
            onClick={() => onChange(state)}
            aria-pressed={active}
            style={{
              border: `1px solid ${active ? style.color : 'var(--glass-border-color)'}`,
              background: active ? 'rgba(255,255,255,0.07)' : 'transparent',
              color: active ? style.color : 'var(--color-text-muted)',
              borderRadius: 'var(--radius-full)',
              padding: '6px 12px',
              fontSize: 'var(--text-xs)',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {style.label}
          </button>
        );
      })}
    </div>
  );
}
